import React from 'react';
import './DataTable.css';

export interface DataTableColumn<T = any> {
  /** Column key */
  key: string;
  /** Column header label */
  label: string;
  /** Column width (CSS value) */
  width?: string;
  /** Render cell content */
  render?: (value: any, row: T, index: number) => React.ReactNode;
}

export interface DataTableProps<T = any> {
  /** Array of columns */
  columns: DataTableColumn<T>[];
  /** Array of data rows */
  data: T[];
  /** Row key extractor */
  rowKey?: (row: T, index: number) => string | number;
  /** Callback when row is clicked */
  onRowClick?: (row: T, index: number) => void;
  /** Show striped rows */
  striped?: boolean;
  /** Show hover effect */
  hoverable?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Empty state content */
  emptyState?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Density preset */
  density?: 'compact' | 'default' | 'comfortable';
}

export const DataTable = <T extends Record<string, any>>({
  columns,
  data,
  rowKey = (_, index) => index,
  onRowClick,
  striped = true,
  hoverable = true,
  loading = false,
  emptyState,
  className = '',
  density = 'default',
}: DataTableProps<T>) => {
  const tableClassNames = [
    'ui-data-table',
    `ui-data-table--${density}`,
    striped && 'ui-data-table--striped',
    hoverable && 'ui-data-table--hoverable',
    onRowClick && 'ui-data-table--clickable',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const getCellContent = (column: DataTableColumn<T>, row: T, index: number) => {
    if (column.render) {
      return column.render(row[column.key], row, index);
    }
    return row[column.key];
  };

  if (loading) {
    return (
      <div className="ui-data-table-wrapper">
        <div className="ui-data-table-loading">Loading...</div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="ui-data-table-wrapper">
        {emptyState || (
          <div className="ui-data-table-empty">No data available</div>
        )}
      </div>
    );
  }

  return (
    <div className="ui-data-table-wrapper">
      <table className={tableClassNames}>
        <thead className="ui-data-table__head">
          <tr className="ui-data-table__row">
            {columns.map((column) => (
              <th
                key={column.key}
                className="ui-data-table__header"
                style={column.width ? { width: column.width } : undefined}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="ui-data-table__body">
          {data.map((row, index) => (
            <tr
              key={rowKey(row, index)}
              className="ui-data-table__row"
              onClick={onRowClick ? () => onRowClick(row, index) : undefined}
            >
              {columns.map((column) => (
                <td key={column.key} className="ui-data-table__cell">
                  {getCellContent(column, row, index)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

DataTable.displayName = 'DataTable';
